import { useState, useEffect, useRef } from 'react';
import { isEqual, cloneDeep } from 'lodash';

import {
  randomString
} from 'shared/utils/Utils';

// the fields state represents the form's nested fields.
// for example, given this fields prop for the JobConfig form:
//
//   [
//     { name: 'name', label: 'Product name', type: 'text', value: 'Package Pricing' },
//     { name: 'product', type: 'inline', fields: [
//       { name: 'pricing_model', label: 'Pricing Model', type: 'select', value: 3 },
//       { name: 'price', label: 'Standard price', type: 'currency' },
//       { name: 'pricing_tiers', label: 'Packages', type: 'section', valueType: 'array', fields: [
//         { type: 'inline', fields: [
//           { name: 'remove_package', type: 'button', label: <CloseIcon />, onClick },
//           { name: 'price', label: 'Package Price', type: 'currency' },
//           { name: 'requirement', label: 'After # of Moments', type: 'number' }
//         ] }
//       ] }
//     ] }
//   ]
//
// the following field state will be created:
//
//   {
//     name: 'Package Pricing',
//     product: {
//       pricing_model: 3,
//       price: null,
//       pricing_tiers: [
//         { price: null, requirement: null }
//       ]
//     }
//   }
//
// note that buttons and irrelevant fields are ignored.
// this field state can be used both for storing values and errors,
// hence it's used by useForm twice
const useFields = props => {
  const {
    // clearValues can be set to true to begin with blank values
    clearValues
  } = props;

  // store files outside of state, otherwise they lose their native File class.
  // store in object for each field by reference key.
  // reference key should be a string beginning with "files-"
  const fileFields = useRef({});

  // setup fields state from fields prop
  const setupFields = () => {
    // clone field objects
    const fields = cloneDeep(props.fields)

    // each field needs to keep a reference to its parent if it has one.
    // we'll use this later to find the value in the form state
    const assignParent = (field, parent) => {
      field.parent = parent;

      if (field.fields) {
        for (let f of field.fields) {
          assignParent(f, field);
        }
      }
    }
    for (let field of fields) {
      assignParent(field, null);
    }

    return parseFieldsObject(fields);
  }

  const parseFieldsObject = fields => {
    // parse fields and render them as an object in the form state
    return fields.reduce((acc, field) => {
      // collapse sections and inline fields without field names into parent level
      let level = parseField(field);
      if (field.type.match('(section|inline)') && !field.name) {
        for (let k in level) {
          acc[k] = level[k];
        }
      } else {
        acc[field.name] = level;
      }
      return acc;
    }, {});
  }

  const parseFieldsArray = fields => {
    // parse fields and render them as an array in the form state
    return fields.filter(field => !ignoreField(field))
      .map(field => parseField(field));
  }

  const parseField = field => {
    // parse field value to set in the form state
    if (field.fields) {
      if (field.valueType === 'array') {
        return parseFieldsArray(field.fields);
      } else {
        return parseFieldsObject(field.fields);
      }
    } else {
      return (!clearValues && field.value) ?? '';
    }
  }

  const ignoreField = field => {
    // just ignore buttons for now
    return field.type === 'button';
  }

  // store fields prop to detect changes (on mount only, note 2nd arg [])
  let currentFieldsProp;
  useEffect(() => {
    currentFieldsProp = cloneDeep(props.fields);
  }, []);

  const [state, setState] = useState({
    fields: setupFields(),
    changed: false,
    lastUpdate: Date.now()
  });

  const resetFields = () => {
    for (let field in fileFields.current) {
      delete fileFields.current[field];
    }

    setState(state => ({
      ...state,
      fields: setupFields(),
      changed: false,
      lastUpdate: Date.now()
    }));
  }

  // reset fields state if fields prop changes
  useEffect(() => {
    if (!isEqual(currentFieldsProp, props.fields)) {
      resetFields();
      currentFieldsProp = cloneDeep(props.fields);
    }
  }, [props.fields]);

  const setFieldValue = (field, value) => {
    // return if the value hasn't changed.
    // there is some extra logic here to treat -999 the same as null and undefined.
    //  - undefined & null both convert to null with == operator while 0 will not, making it a valid value
    // hoping to smooth this out at some point so that -999 isn't needed,
    // but currently null is causing issues with the select field
    let oldValue = getFieldValue(field);
    if (
      oldValue === value || (
        (oldValue == null || oldValue === -999) &&
        (value == null || value === -999)
      )
    ) {
      return;
    }

    // if the value is a File or FileList, then store outside of state,
    // and only store a reference to the file in the state.
    // this is to prevent React from cloning the file objects and destroying
    // their native class.
    if (value instanceof File || value instanceof FileList) {
      let key = 'files-' + randomString();
      fileFields.current[key] = value;
      value = key;
    }

    // to update the value, we can just get the field state and then
    // pass the fields object back into setFields (since we modified that object).
    // kind of funky but it works for now
    let fieldLevel = getFieldLevel(field);
    if (fieldLevel) {
      fieldLevel[field.name] = value;
    }

    // determine whether the value has changed from the original
    let fieldChanged = field.value !== value;
    const changed = !state.changed && fieldChanged;

    setState(state => ({
      ...state,
      fields: state.fields,
      changed,
      lastUpdate: Date.now()
    }));
  }

  const getFieldValue = field => {
    // get the value from the field state unless we're ignoring this field
    if (!ignoreField(field)) {
      let fieldLevel = getFieldLevel(field);
      if (fieldLevel) {
        let value = fieldLevel[field.name];

        // if empty string or undefined then return null
        if (value === '' || value === undefined) {
          return null;
        }

        if (typeof value === 'number' && field.type == 'currency') {
          value = value.toFixed(2)
        }

        // if fileFields key, then return file or files
        if (typeof value === 'string' && value.match(/^files-/)) {
          return fileFields.current[value];
        }

        return value;
      }
    }
    return field.value;
  }

  const getFieldLevel = field => {
    // find nested field in form state.
    // get the field's parents then look it up in the form state.
    let parents = getFieldParents(field);
    let fieldLevel = state.fields;
    for (let key of parents.reverse()) {
      fieldLevel = fieldLevel[key];
    }

    // fieldLevel is now the nested object in which this field resides.
    // if this is an array of fields (has many association)
    // then we need to find the field in the array and determine its index.
    // otherwise we can just look up the field name.
    // note - this is why ignored fields (buttons) need to be in the state,
    // so that the index here is accurate.
    if (Array.isArray(fieldLevel)) {
      for (let i = 0; i < field.parent.parent.fields.length; i++) {
        if (field.parent.parent.fields[i] === field.parent) {
          return fieldLevel[i];
        }
      }
    } else {
      return fieldLevel;
    }
  }

  const getFieldParents = field => {
    // return parent field names to help with state lookup

    // get parent fields
    let parents = [];
    while (field.parent) {
      parents.push(field.parent);
      field = field.parent;
    }

    // map field names and filter out fields without names.
    // any fields without names will be ignored and excluded from
    // the object heiarchy (they'll essentially flatten into the field above)
    return parents.map(p => p.name)
      .filter(name => !!name);
  }

  return {
    fields: state.fields,
    changed: state.changed,
    lastUpdate: state.lastUpdate,
    resetFields,
    setFieldValue,
    getFieldValue,
    fileFields
  }
}

// repurpose useFields for storing and retrieving errors
const useFieldErrors = props => {
  const {
    fields: fieldErrors,
    lastUpdate: lastErrorsUpdate,
    resetFields: resetFieldErrors,
    setFieldValue: setFieldError,
    getFieldValue: getFieldError
  } = useFields({
    ...props,
    clearValues: true
  });

  const clearFieldError = field => {
    let error = getFieldError(field);
    if (error) {
      setFieldError(field, null);
    }
  }

  return {
    fieldErrors,
    lastErrorsUpdate,
    resetFieldErrors,
    setFieldError,
    getFieldError,
    clearFieldError
  }
}

const useForm = props => {
  const {
    formatParams
  } = props;

  const {
    fields,
    changed,
    lastUpdate,
    resetFields,
    setFieldValue,
    getFieldValue,
    fileFields
  } = useFields(props);

  // manage errors for each field
  const {
    //fieldErrors,
    //lastErrorsUpdate,
    //resetFieldErrors,
    setFieldError,
    getFieldError,
    clearFieldError
  } = useFieldErrors(props);

  // manage main form error
  const [formError, setFormError] = useState(null);

  const clearFormError = () => setFormError(null);

  const onChange = (field, event, value = null, selectedOption = null) => {
    // detect if checkbox has been checked
    if (!value) {
      if (field.type === 'checkbox') {
        value = event?.target?.checked;
      } else if (field.type === 'datetime') {
        // the DateTimePicker passes the value as the 2nd arg.
        // convert value from dayjs obj to timestamp string
        value = event?.toISOString();
      } else {
        value = event?.target?.value;
      }
    }

    // fire field onChange callback if set.
    // note this might cause problems
    if (field.onChange) {
      field.onChange(value, selectedOption);
    }

    // todo: probably remove the below option, too problematic
    // fire form-level onChange callback if set
    if (props.onChange) {
      // only some field types should update the parent.
      // this can be changed if needed, but as of now only selects need to
      // update the parent, as the parent form might want to toggle fields
      // based on the selected options.
      // text fields don't really need to update the parent unless the parent
      // wants to render the UI based on the value changes.
      // this can be done but then the text field needs to be a controlled input,
      // where its value is set via a prop from the parent,
      // and this introduces a little but of lag for the user entering text.
      // so only updating the parent for controlled inputs for now
      if (['select', 'datetime', 'autocomplete'].includes(field.type)) {
        props.onChange(fields);
      }
    }

    // clear field error
    clearFieldError(field);

    // set value based on valueType.
    // if field.multiple is true,
    // then set each array element based on valueType
    switch (field.valueType) {
      case 'integer':
	if (field.multiple) {
	  // selectedOption is an array of selected options
	  setFieldValue(field, selectedOption.map(opt => parseInt(opt.value)));
	} else {
	  setFieldValue(field, parseInt(value));
	}
	break;
      case 'number':
        setFieldValue(field, value);
        break;
      case 'boolean':
        setFieldValue(field, !!value);
        break;
      default:
        // treat "type: checkbox" as "valueType: boolean"
        if (field.type === 'checkbox') {
          setFieldValue(field, !!value);
        } else {
          setFieldValue(field, value);
        }
    }
  }

  const validateFields = () => {
    // these needs to be fixed to handle nested fields
    let valid = true;
    for (let field of props.fields) {
      if (field.required && !fields[field.name]) {
        setFieldError(field, 'Field required');
        valid = false;
      }
    }

    if (!valid) {
      setFormError('Please fill the required fields.');
    }

    return valid;
  }

  const buildParams = () => {
    // recursively deep copy fields state to build params
    const buildParamsLevel = fieldsLevel => {
      let paramsLevel = {};
      let fieldTypes = {};

      for (let field of props.fields) {
        fieldTypes[field.name] = field.type;
      }

      for (let field in fieldsLevel) {
        // if the field contains an array,
        // then it could be either an array of values (from a multiple select)
        // or an array of inner fields.
        // if it's the latter, then recursively parse the inner fields,
        // otherwise just set the array as the param value
        if (Array.isArray(fieldsLevel[field])) {
          // if this is an array of inner fields, then the first element would be an object.
          // otherwise it must be an array of values
          let isInnerFields = typeof fieldsLevel[field][0] === 'object';

          if (isInnerFields) {
            paramsLevel[field] = [];
            for (let el of fieldsLevel[field]) {
              paramsLevel[field].push(buildParamsLevel(el));
            }
          } else {
                  paramsLevel[field] = fieldsLevel[field];
          }

        } else if (typeof fieldsLevel[field] === 'object') {
          paramsLevel[field] = buildParamsLevel(fieldsLevel[field]);

        } else if (typeof fieldsLevel[field] === 'string' && fieldsLevel[field].match(/^files-/)) {
          paramsLevel[field] = fileFields.current[fieldsLevel[field]];

        } else if (typeof fieldsLevel[field] === 'number' && fieldTypes[field] == 'currency') {
          paramsLevel[field] = fieldsLevel[field].toFixed(2);

        } else {
          paramsLevel[field] = fieldsLevel[field];

          // if field value is an empty string, then set to null.
          // might want to revisit this
          if (paramsLevel[field] === '') {
            paramsLevel[field] = null;
          }

          // currently using -999 as null value in selects since actual nulls are
          // currently causing problems. Updating params accordingly here
          // TODO: null vs 0 could cause problems in certain contexts (i.e. disabled kiosk prints)?
          if (paramsLevel[field] === -999) {
            paramsLevel[field] = null;
          }
        }
      }

      return paramsLevel;
    }

    return buildParamsLevel(fields);
  }

  const onSubmit = async () => {
    let fieldsValid = validateFields();
    if (!fieldsValid) {
      // validateFields sets the field and form errors
      return;
    }

    let params = buildParams();

    if (formatParams) {
      params = formatParams(params);
    }

    // add is_portal flag to params.
    // the API currently expects this for jobs and customers.
    // this isn't the best spot for this but it works for now
    params.is_portal = true;
    try {
      await props.onSubmit(params)

      // set the new values on the fields to determine new changes
      // ?? - look into what this is doing
      //for (let k in params) {
      //  let field = props.fields.find(f => f.name === k);
      //  if (field) {
      //    field.value = params[k];
      //  }
      //}

      // close the modal on successful submit.
      // seems like in most cases the user would want this,
      // though there are other actions the modal supports besides the form
      props.onClose();

    } catch (error) {
      console.error(error);
      setFormError(error);
    }
  }

  return {
    changed,
    lastUpdate,
    getFieldValue,
    setFieldValue,
    getFieldError,
    formError,
    clearFormError,
    setFormError,
    onChange,
    onSubmit,
    resetFields,
    fields
  }
}

export default useForm;
